import React, { useEffect, useState } from "react";

import themeVariables from "@Theme/exportColor.module.scss";
import styles from "./RangeSliderTwoHandles.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useWhitelabel from "hooks/useWhitelabel";

interface Props {
  min: number;
  max: number;
  valueOne: number;
  valueTwo: number;
  step: number;
  handleOnChange?: any;
  handleOnDragEnd?: any;
  reset?: number;
  containerStyles?: string;
  actualMinVal?: number;
  openingSource?: string;
  showPriceOnSliders?: boolean;
}
const RangeSliderTwoHandles: React.FC<Props> = (props) => {
  const {
    min,
    max,
    step,
    valueOne,
    valueTwo,
    handleOnChange,
    handleOnDragEnd,
    reset,
    containerStyles,
    actualMinVal = 0,
    openingSource,
    showPriceOnSliders = false,
  } = props;
  const [value1, setValue1] = useState<any>(valueOne);
  const [value2, setValue2] = useState<any>(valueTwo);

  const { isWhitelabel, whitelabelColors } = useWhitelabel();

  useEffect(() => {
    setValue1(valueOne);
    setValue2(valueTwo);
  }, [reset]);

  const getValuePosition = (value: number) => ((value - min) / (max - min)) * 100;
  const getActualValue = (value: number) => value + actualMinVal;

  const onChangeValue1 = (e: any) => {
    if (e.target.value !== value2) {
      setValue1(e.target.value);
      if (handleOnChange) {
        handleOnChange([Math.min(e.target.value, value2), Math.max(e.target.value, value2)]);
      }
    }
  };

  const onChangeValue2 = (e: any) => {
    if (e.target.value !== value1) {
      const moveMinSlider = e.target.value < value2 / 2;
      // eslint-disable-next-line no-unused-expressions
      moveMinSlider ? setValue1(e.target.value) : setValue2(e.target.value);
      if (handleOnChange) {
        handleOnChange(
          moveMinSlider
            ? [Math.max(e.target.value, value1), value2]
            : [Math.min(e.target.value, value1), Math.max(e.target.value, value1)],
        );
      }
    }
  };

  const onTouchEndValue1 = (e: any) => {
    if (handleOnDragEnd) {
      handleOnDragEnd([Math.min(e.target.value, value2), Math.max(e.target.value, value2)]);
    }
  };

  const onTouchEndValue2 = (e: any) => {
    if (handleOnDragEnd) {
      handleOnDragEnd([Math.min(value1, e.target.value), Math.max(value1, e.target.value)]);
    }
  };

  const getBackground = () => {
    const valDiff = Math.abs(value1 - value2);
    const fillPer = Math.round((100 * (valDiff - min)) / (max - min));
    const minValue = Math.min(parseInt(value2, 10), parseInt(value1, 10));
    const minFillPer = Math.round((100 * (minValue - min)) / (max - min)) - 1;

    return `linear-gradient(to right, ${themeVariables.neutral2} 0%, ${
      themeVariables.neutral2
    } ${minFillPer}%, ${
      isWhitelabel ? whitelabelColors?.accessible_brand_color : themeVariables.primary4
    } ${minFillPer}%, ${
      isWhitelabel ? whitelabelColors?.accessible_brand_color : themeVariables.primary4
    } ${minFillPer + fillPer}%, ${themeVariables.neutral2} ${minFillPer + fillPer}%)`;
  };

  useEffect(() => {
    setValue1(valueOne);
    setValue2(valueTwo);
  }, [valueOne, valueTwo]);

  return (
    <div
      className={formatClassNames(styles.rangeSliderTwoHandlesContainer, containerStyles)}
      style={
        {
          "--value1-position": `${getValuePosition(parseInt(value1, 10))}%`,
          "--value2-position": `${getValuePosition(parseInt(value2, 10))}%`,
          "--value1": `'£${getActualValue(parseInt(value1, 10))}'`,
          "--value2": `'£${getActualValue(parseInt(value2, 10))}'`,
        } as React.CSSProperties
      }
    >
      <input
        value={value1}
        style={{ background: getBackground() }}
        className={formatClassNames(
          styles.slider,
          openingSource && styles[openingSource],
          !showPriceOnSliders && styles.showPriceOnSliders,
        )}
        min={min}
        max={max}
        step={step}
        type="range"
        onChange={onChangeValue1}
        onTouchEnd={onTouchEndValue1}
        onDragEnd={onTouchEndValue1}
        onMouseUp={onTouchEndValue1}
        data-testid="range-left-input"
      />
      <input
        value={value2}
        style={{ background: getBackground() }}
        className={formatClassNames(
          styles.slider,
          openingSource && styles[openingSource],
          !showPriceOnSliders && styles.showPriceOnSliders,
        )}
        min={min}
        max={max}
        step={step}
        type="range"
        onChange={onChangeValue2}
        onTouchEnd={onTouchEndValue2}
        onDragEnd={onTouchEndValue2}
        onMouseUp={onTouchEndValue2}
        data-testid="range-right-input"
      />
    </div>
  );
};

RangeSliderTwoHandles.defaultProps = {
  handleOnChange: null,
  handleOnDragEnd: null,
  reset: 0,
};

export default wrapperHOC(RangeSliderTwoHandles, {
  componentName: "RangeSliderTwoHandles-Global",
  showForChina: true,
});
